import { useState, useRef, useEffect } from 'react';
import { useSearchContext } from '../../../contexts/SearchContext';

export type ViewMode = 'list' | 'map';

export interface UseViewModeProps {
  initialLayout?: string | null;
}

export interface UseViewModeReturn {
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;
}

/** Reads the `view` query param — the URL is the source of truth for which layout is showing. */
function readViewModeFromUrl(): ViewMode | null {
  if (typeof window === 'undefined') return null;
  const view = new URLSearchParams(window.location.search).get('view');
  if (view === 'map') return 'map';
  if (view === 'list') return 'list';
  return null;
}

/**
 * Hook to manage view mode state (grid/list/map)
 * Syncs with SearchContext and localStorage
 */
export function useViewMode({ initialLayout }: UseViewModeProps = {}): UseViewModeReturn {
  const { viewMode: contextViewMode, setViewMode: setContextViewMode } = useSearchContext();

  // Initialize viewMode from the URL `view` param — it's what the user is actually looking at
  // (e.g. after a browser Back that restored `?view=map`). `initialLayout` (SSR, itself derived
  // from the same URL) is the fallback for the very first render before `window` exists.
  // 'grid' is normalized to 'list'.
  const [viewMode, setViewModeState] = useState<ViewMode>(() => {
    const fromUrl = readViewModeFromUrl();
    if (fromUrl) return fromUrl;
    if (initialLayout === 'map') return 'map';
    return 'list';
  });

  // Sync local viewMode to context on mount and when it changes
  // Use useRef to prevent infinite loops - only sync when viewMode actually changes
  const prevViewModeRef = useRef(viewMode);
  const hasSyncedRef = useRef(false);
  useEffect(() => {
    // One-time mount sync: this hook and SearchContext restore from overlapping but not
    // identical sources (URL param, layout cookie, localStorage), so they can disagree on
    // load. The rendered layout (this hook's value — it's what CustomHits/SearchResults
    // actually show) wins: searches must run in the mode the user is looking at, otherwise a
    // restored map view sits on a list-mode search and never fetches pins/facets.
    if (!hasSyncedRef.current) {
      hasSyncedRef.current = true;
      prevViewModeRef.current = viewMode;
      if (contextViewMode !== viewMode) {
        setContextViewMode(viewMode);
      }
      return;
    }
    // After mount: only sync when this hook's viewMode changed (not when contextViewMode did).
    if (prevViewModeRef.current !== viewMode && contextViewMode !== viewMode) {
      setContextViewMode(viewMode);
      prevViewModeRef.current = viewMode;
    }
  }, [viewMode, contextViewMode, setContextViewMode]);

  // Browser back/forward (and any other same-tab URL change) must flip the rendered layout even
  // when nothing else re-mounts this hook. SearchContext already writes `view=map`/no-param into
  // the URL on toggle and patches history.pushState/replaceState to fire 'urlchange' — mirror its
  // popstate handling here so this hook's viewMode (the one CustomHits/SearchResults actually
  // render off) can't drift from what the address bar says. Without this, the map/list layout
  // that's shown was governed by whatever this hook's state happened to be, not by the URL.
  useEffect(() => {
    const syncFromUrl = (): void => {
      const fromUrl = readViewModeFromUrl();
      if (fromUrl && fromUrl !== viewMode) {
        setViewModeState(fromUrl);
      }
    };
    window.addEventListener('popstate', syncFromUrl);
    window.addEventListener('urlchange', syncFromUrl);
    return () => {
      window.removeEventListener('popstate', syncFromUrl);
      window.removeEventListener('urlchange', syncFromUrl);
    };
  }, [viewMode]);

  // Wrapper function to update viewMode and sync to context. The URL itself (the actual source of
  // truth) is updated by SearchContext's own URL-sync effect, which reacts to context viewMode.
  const setViewMode = (newViewMode: ViewMode) => {
    setViewModeState(newViewMode);
    setContextViewMode(newViewMode);
  };

  return {
    viewMode,
    setViewMode,
  };
}
